import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, borderRadius } from '../styles/designTokens';
import { CustomerService } from '../services/CustomerService';
import { PaymentHistory } from '../models/CustomerModels';
import { formatCurrency, formatDate } from '../utils/numberFormatter';

// 모바일 결제 내역 컨테이너
const MobilePaymentContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
`;

// 모바일 결제 내역 헤더
const MobilePaymentHeader = styled.div`
  background: ${colors.semantic.background.secondary};
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 결제 내역 제목
const MobilePaymentTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0;
  line-height: 1.2;
`;

// 모바일 결제 카드 - 컴팩트 디자인
const MobilePaymentCard = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${colors.secondary[100]};
  
  &:last-child {
    border-bottom: none;
  }
`;

// 모바일 결제 카드 헤더 - 컴팩트
const MobilePaymentCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;

// 모바일 결제 상품명 - 컴팩트
const MobilePaymentProduct = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0;
  flex: 1;
  line-height: 1.2;
`;

// 모바일 결제 금액 - 컴팩트
const MobilePaymentAmount = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.success[600]};
  text-align: right;
  margin-left: 6px;
`;

// 모바일 결제 정보 그리드 - 컴팩트
const MobilePaymentInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  font-size: 11px;
  margin-top: 2px;
`;

// 모바일 결제 정보 아이템 - 컴팩트
const MobilePaymentInfoItem = styled.div`
  color: ${colors.semantic.text.tertiary};

  .label {
    font-weight: 500;
    margin-bottom: 1px;
    font-size: 10px;
  }

  .value {
    color: ${colors.semantic.text.secondary};
    font-size: 11px;
    line-height: 1.2;
  }
`;

// 모바일 로딩 스피너
const MobileLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  
  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid ${colors.semantic.border.primary};
    border-top: 2px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 모바일 에러 메시지
const MobileErrorMessage = styled.div`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  color: ${colors.error[600]};
  padding: 12px 16px;
  border-radius: ${borderRadius.md};
  text-align: center;
  font-size: 12px;
  margin: 16px;
  line-height: 1.3;
`;

// 모바일 빈 상태 메시지
const MobileEmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: ${colors.semantic.text.tertiary};

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: ${colors.semantic.text.secondary};
    margin: 0 0 6px 0;
  }
  
  p {
    font-size: 12px;
    margin: 0;
    line-height: 1.3;
  }
`;

// 모바일 결제 내역 테이블 컴포넌트
interface PaymentHistoryTableMobileProps {
  userIdx: number;
}

const PaymentHistoryTableMobile: React.FC<PaymentHistoryTableMobileProps> = ({ userIdx }) => {
  const [payments, setPayments] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('📱 모바일 결제 내역 API 호출 시작:', userIdx);
        const response = await CustomerService.getPaymentHistory({
          userIdx,
          page: 1,
          limit: 15
        });

        console.log('📱 모바일 결제 내역 API 응답 성공:', response);
        setPayments(response.data || []);
      } catch (err: any) {
        console.error('📱 모바일 결제 내역 조회 실패:', err);
        setError(err.message || '결제 내역을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    if (userIdx) {
      fetchPayments();
    }
  }, [userIdx]);

  if (loading) {
    return (
      <MobilePaymentContainer>
        <MobilePaymentHeader>
          <MobilePaymentTitle>결제 내역</MobilePaymentTitle>
        </MobilePaymentHeader>
        <MobileLoadingContainer>
          <div className="spinner" />
        </MobileLoadingContainer>
      </MobilePaymentContainer>
    );
  }

  if (error) {
    return (
      <MobilePaymentContainer>
        <MobilePaymentHeader>
          <MobilePaymentTitle>결제 내역</MobilePaymentTitle>
        </MobilePaymentHeader>
        <MobileErrorMessage>{error}</MobileErrorMessage>
      </MobilePaymentContainer>
    );
  }

  return (
    <MobilePaymentContainer>
      <MobilePaymentHeader>
        <MobilePaymentTitle>
          결제 내역 {payments && payments.length > 0 && `(${payments.length}건)`}
        </MobilePaymentTitle>
      </MobilePaymentHeader>

      {!payments || payments.length === 0 ? (
        <MobileEmptyState>
          <h3>결제 내역이 없습니다</h3>
          <p>아직 결제한 내역이 없습니다.</p>
        </MobileEmptyState>
      ) : (
        payments.map((payment, index) => (
          <MobilePaymentCard key={`${payment.승인일시}-${index}`}>
            <MobilePaymentCardHeader>
              <MobilePaymentProduct>{payment.결제상품명}</MobilePaymentProduct>
              <MobilePaymentAmount>{formatCurrency(payment.결제금액)}</MobilePaymentAmount>
            </MobilePaymentCardHeader>

            <MobilePaymentInfoGrid>
              <MobilePaymentInfoItem>
                <div className="label">승인일시</div>
                <div className="value">{formatDate(payment.승인일시)}</div>
              </MobilePaymentInfoItem>
              <MobilePaymentInfoItem>
                <div className="label">결제수단</div>
                <div className="value">{payment.결제수단}</div>
              </MobilePaymentInfoItem>
              <MobilePaymentInfoItem>
                <div className="label">카드사</div>
                <div className="value">{payment.카드사}</div>
              </MobilePaymentInfoItem>
              <MobilePaymentInfoItem>
                <div className="label">카드번호</div>
                <div className="value">{payment.카드번호}</div>
              </MobilePaymentInfoItem>
              <MobilePaymentInfoItem>
                <div className="label">충전포인트</div>
                <div className="value">{formatCurrency(payment.충전포인트)}P</div>
              </MobilePaymentInfoItem>
            </MobilePaymentInfoGrid>
          </MobilePaymentCard>
        ))
      )}
    </MobilePaymentContainer>
  );
};

export default PaymentHistoryTableMobile;
